import type { MapNode } from './jsx';

export type GateTarget = 'Town' | 'Field' | 'Dungeon';

export interface PropDef {
  image: string;            // texture key (TEX.*)
  x: number; y: number;     // pixel position (sprite center)
  w: number; h: number;     // displayed size in px
  depth?: number;           // override (default = y + h/2, i.e. sprite's visual base for y-sort)
  tween?: 'flicker' | 'breathe';
  tint?: number;
  flipX?: boolean;
  flipY?: boolean;
  alpha?: number;
  rotation?: number;        // degrees, clockwise; default 0
}

export type CollisionDef =
  | { kind: 'rect'; x: number; y: number; w: number; h: number }
  | { kind: 'tiles'; tiles: Array<[number, number]> };

export interface GateDef {
  x: number; y: number; radius: number;
  target: GateTarget;
  targetMapId: number;
  spawnX: number; spawnY: number;
  minLv: number;
  label?: string;
}

export interface MapModule {
  id: string;
  width: number;             // pixels
  height: number;            // pixels
  tileSize: number;
  spawn: { x: number; y: number };
  theme?: string;
  base?: { image: string };
  procedural?: { theme: string };  // when set, ground is painted by buildFieldTilemap(theme)
  props: PropDef[];
  collisions: CollisionDef[];
  gates: GateDef[];
}

function asNum(v: unknown, fallback = 0): number {
  return typeof v === 'number' ? v : fallback;
}
function asStr(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}

export function compile(root: MapNode): MapModule {
  if (root.type !== 'map') {
    throw new Error(`Map root must be <Map>, got <${root.type}>`);
  }
  const p = root.props;
  const spawn = (p.spawn as { x: number; y: number } | undefined) ?? { x: 0, y: 0 };
  const mod: MapModule = {
    id: asStr(p.id, 'unknown'),
    width: asNum(p.width, 1920),
    height: asNum(p.height, 1280),
    tileSize: asNum(p.tileSize, 32),
    spawn,
    theme: typeof p.theme === 'string' ? p.theme : undefined,
    base: undefined,
    procedural: undefined,
    props: [],
    collisions: [],
    gates: [],
  };

  for (const child of root.children) {
    const cp = child.props;
    switch (child.type) {
      case 'base':
        mod.base = { image: asStr(cp.image) };
        break;
      case 'procedural':
        mod.procedural = { theme: asStr(cp.theme) };
        break;
      case 'prop':
        mod.props.push({
          image: asStr(cp.image),
          x: asNum(cp.x), y: asNum(cp.y),
          w: asNum(cp.w, 64), h: asNum(cp.h, 64),
          depth: typeof cp.depth === 'number' ? cp.depth : undefined,
          tween: cp.tween === 'flicker' || cp.tween === 'breathe' ? cp.tween : undefined,
          tint: typeof cp.tint === 'number' ? cp.tint : undefined,
          flipX: cp.flipX === true,
          flipY: cp.flipY === true,
          alpha: typeof cp.alpha === 'number' ? cp.alpha : undefined,
          rotation: typeof cp.rotation === 'number' ? cp.rotation : undefined,
        });
        break;
      case 'collision': {
        const rect = cp.rect as { x: number; y: number; w: number; h: number } | undefined;
        const tiles = cp.tiles as Array<[number, number]> | undefined;
        if (rect) mod.collisions.push({ kind: 'rect', ...rect });
        if (tiles && tiles.length) mod.collisions.push({ kind: 'tiles', tiles });
        break;
      }
      case 'gate':
        mod.gates.push({
          x: asNum(cp.x), y: asNum(cp.y),
          radius: asNum(cp.radius, 48),
          target: (cp.target as GateTarget) ?? 'Field',
          targetMapId: asNum(cp.targetMapId),
          spawnX: asNum(cp.spawnX), spawnY: asNum(cp.spawnY),
          minLv: asNum(cp.minLv, 1),
          label: typeof cp.label === 'string' ? cp.label : undefined,
        });
        break;
      default:
        // Ignore unknown nodes (forward-compat).
        break;
    }
  }

  return mod;
}

// Convert collision rects/tiles into pixel-rect blockers consumed by
// FieldScene's existing isBlocked()/collide() helpers.
export interface PixelBlocker { x: number; y: number; w: number; h: number }

export function compileBlockers(mod: MapModule): PixelBlocker[] {
  const ts = mod.tileSize;
  const out: PixelBlocker[] = [];
  for (const c of mod.collisions) {
    if (c.kind === 'rect') {
      out.push({ x: c.x, y: c.y, w: c.w, h: c.h });
    } else {
      for (const [tx, ty] of c.tiles) {
        out.push({ x: tx * ts, y: ty * ts, w: ts, h: ts });
      }
    }
  }
  return out;
}
